import { FormikHelpers } from "formik";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import { IEditableSectionProps } from "@/components/EditableSection";
import {
  createBankingDetails,
  deleteBankingDetail,
  fetchBankingDetails,
  ISupplierBankingDetails,
  makeBankingDetailPrimary,
  putBankingDetails,
} from "@/data/companies";
import { useCompanyContext } from "@/hooks/User";
import { useToasts } from "@/views/components/Toast/useToasts";
import { CompanyBankingDetailsPreview } from "@/views/Settings/CompanySettings/BankingDetails/CompanyBankingDetailsPreview";
import { bankingDetailsSchema } from "@/views/Settings/CompanySettings/BankingDetails/validationSchema";

import { CompanyBankingSectionEdit } from "./CompanyBankingSectionEdit";

interface ISupplierBankingSectionProps {
  editableProps: IEditableSectionProps;
}

export interface IBankingDetails {
  bankingDetails: ISupplierBankingDetails[];
}

export const bankDetailInitialValue = {
  id: undefined,
  paymentTypeId: -1,
  countryCode: "",
  bankNumber: "",
  iban: "",
  swift: "",
  factoringCompany: false,
  primary: false,
  paymentName: "",
  status: "ACTIVE",
};

const initialValues: ISupplierBankingDetails[] = [bankDetailInitialValue];

export const CompanyBankingSection = ({ editableProps }: ISupplierBankingSectionProps) => {
  const Company = useCompanyContext();
  const { t } = useTranslation("session");
  const { addToast } = useToasts();

  const { data: fetchedBankingDetails, refetch } = useQuery(
    `/api/companies/company-payment-type/${Company.id}`,
    () => fetchBankingDetails(Company.id),
    {
      enabled: !!Company.id,
    },
  );

  const bankingDetails = useMemo(() => {
    return fetchedBankingDetails?.length ? fetchedBankingDetails : initialValues;
  }, [fetchedBankingDetails]);

  const handleMakePrimary = async (data: ISupplierBankingDetails) => {
    try {
      if (data.id) {
        await makeBankingDetailPrimary(data.id, {
          ...data,
          primary: true,
        });

        refetch();
      }
    } catch (error) {
      addToast({
        text: t("USERS.SETTINGS.BANKING_DETAILS.FORM.TOAST.PRIMARY"),
        type: "danger",
      });
    }
  };

  const handleUpdate = async (values: ISupplierBankingDetails[]) => {
    await Promise.all(
      values.map(async bankingDetail => {
        if (bankingDetail.status === "INACTIVE") {
          await deleteBankingDetail(bankingDetail.id!);
        } else {
          await putBankingDetails(bankingDetail.id!, bankingDetail);
        }
      }),
    );
  };

  const handleCreate = async (values: ISupplierBankingDetails[]) => {
    await Promise.all(
      values
        .filter(bankingDetail => bankingDetail.status === "ACTIVE")
        .map(async bankingDetail => {
          await createBankingDetails(Company.id, bankingDetail);
        }),
    );
  };

  const getValuesByAction = (values: IBankingDetails) =>
    values.bankingDetails.reduce<{
      valuesToUpdate: ISupplierBankingDetails[];
      valuesToCreate: ISupplierBankingDetails[];
    }>(
      (acc, item1) => {
        if (fetchedBankingDetails?.some(item2 => item2.id === item1.id)) {
          acc.valuesToUpdate.push(item1);
        } else {
          acc.valuesToCreate.push(item1);
        }

        return acc;
      },
      { valuesToUpdate: [], valuesToCreate: [] },
    );

  const handleSubmit = async (values: IBankingDetails, actions: FormikHelpers<IBankingDetails>) => {
    const action = fetchedBankingDetails?.length ? "update" : "creation";

    try {
      const { valuesToCreate, valuesToUpdate } = getValuesByAction(values);

      await handleUpdate(valuesToUpdate);
      await handleCreate(valuesToCreate);

      actions.setSubmitting(false);
      editableProps.handleCancelClick();
      await refetch();

      addToast({
        text: t("USERS.SETTINGS.BANKING_DETAILS.FORM.TOAST.SUCCESS", { action }),
        type: "success",
      });
    } catch (e) {
      addToast({
        text: t("USERS.SETTINGS.BANKING_DETAILS.FORM.TOAST.FAILURE", { action }),
        type: "danger",
      });
    }
  };

  return (
    <>
      {editableProps.editMode ? (
        <CompanyBankingSectionEdit
          source={Company}
          onSubmit={handleSubmit}
          editableProps={editableProps}
          validationSchema={bankingDetailsSchema}
          onCancel={editableProps.handleCancelClick}
          initialValues={{ bankingDetails: bankingDetails }}
        />
      ) : (
        <CompanyBankingDetailsPreview makePrimary={handleMakePrimary} details={fetchedBankingDetails} />
      )}
    </>
  );
};
